import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Button, Card, Modal, Input, Row, Col, Checkbox } from "antd";
import { ReactComponent as CloseIcon } from "../../Icons/vuesax/close-square.svg";
import { ReactComponent as SearchIcon } from "../../Icons/vuesax/search-normal.svg";
import { ReactComponent as AddIcon } from "../../Icons/vuesax/add.svg";
import DateOfDictationFilter from "../filters/DateOfDictationFilter";
import DateOfServiceFilter from "../filters/DateOfServiceFilter";
import DictationFilter from "../filters/DictationFilter";
import JobStatusFilter from "../filters/JobStatusFilter";
import LocationFilter from "../filters/LocationFilter";
import PatientFilter from "../filters/PatientFilter";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import dayjs, { Dayjs } from "dayjs";
import { showErrorToast } from "helpers/utils";
import JobGrid from "./JobGrid";
import isoWeek from "dayjs/plugin/isoWeek";
import { DefaultArchiveFilters } from "components/DefaultArchiveFilters";
import { DefaultDateType } from "api/services/models/types";
dayjs.extend(isoWeek);

interface ArchiveFiltersProps {
  selectedServiceDateFrom?: Dayjs | null;
  selectedServiceDateTo?: Dayjs | null;
  selectedDictationDateFrom?: Dayjs | null;
  selectedDictationDateTo?: Dayjs | null;
  selectedFirstName?: string;
  selectedLastName?: string;
  selectedMRN?: string;
  selectedDOB?: Dayjs | null;
  selectedProviders?: string[];
  selectedReportTypes?: string[];
  selectedJobId?: string;
  selectedRegions?: string[];
  selectedLocations?: string[];
  selectedJobStatuses?: any[]; // Adjust type as necessary
  defaultDateType?: DefaultDateType;
  renderData?: Dayjs | null;
  onShowResultsChange: (value: boolean) => void;
}
const ArchiveFilters = forwardRef(({
  selectedServiceDateFrom = null,
  selectedServiceDateTo = null,
  selectedDictationDateFrom = null,
  selectedDictationDateTo = null,
  selectedFirstName = "",
  selectedLastName = "",
  selectedMRN = "",
  selectedDOB = null,
  selectedProviders = [],
  selectedReportTypes = [],
  selectedJobId = "",
  selectedRegions = [],
  selectedLocations = [],
  selectedJobStatuses = [],
  defaultDateType = DefaultDateType.None,
  renderData,
  onShowResultsChange
}: ArchiveFiltersProps, ref) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewName, setViewName] = useState("");
  const hasPageRendered = useRef(false);
  const [showResults, setShowResults] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<any>(null);

  //DateOfServiceFilter
  const [serviceDateFrom, setServiceDateFrom] = useState<Dayjs | null>(selectedServiceDateFrom);
  const [serviceDateTo, setServiceDateTo] = useState<Dayjs | null>(selectedServiceDateTo);
  //DateOfDictationFilter
  const [dictationDateFrom, setDictationDateFrom] = useState<Dayjs | null>(selectedDictationDateFrom);
  const [dictationDateTo, setDictationDateTo] = useState<Dayjs | null>(selectedDictationDateTo);
  //PatientFilter
  const [firstName, setFirstName] = useState(selectedFirstName);
  const [lastName, setLastName] = useState(selectedLastName);
  const [mrn, setMrn] = useState(selectedMRN);
  const [dob, setDob] = useState<Dayjs | null>(selectedDOB);
  //DictationFilter
  const [providers, setProviders] = useState<string[] | undefined>(selectedProviders);
  const [reportTypes, setReportTypes] = useState<string[] | undefined>(selectedReportTypes);
  const [jobId, setJobId] = useState(selectedJobId);
  //LocationFilter
  const [regions, setRegions] = useState<string[] | undefined>(selectedRegions);
  const [locations, setLocations] = useState<string[] | undefined>(selectedLocations);
  //JobStatusFilter
  const [jobStatus, setJobStatus] = useState<any[] | undefined>(selectedJobStatuses);

  const [selectedDefaultDateType, setSelectedDefaultDateType] = useState<DefaultDateType>(defaultDateType);
  const [defaultDateTypeChecked, setDefaultDateTypeChecked] = useState<boolean>(false);

  const { accountId, webDUser } = useTDAuthentication();
  const userId = webDUser?.user.userId;

  //Key props to force re-mounting of filter components when these values change
  const dateOfServiceFilter = JSON.stringify(selectedServiceDateFrom) + JSON.stringify(selectedServiceDateTo);
  const dateOfDictationFilter = JSON.stringify(selectedDictationDateFrom) + JSON.stringify(selectedDictationDateTo);
  const patientFilter = selectedFirstName + selectedLastName + selectedMRN + String(selectedDOB);
  const dictationFilter = JSON.stringify(selectedProviders) + JSON.stringify(selectedReportTypes) + selectedJobId;
  const locationFilter = JSON.stringify(selectedRegions) + JSON.stringify(selectedLocations);
  const jobStatusFilter = JSON.stringify(selectedJobStatuses);

  useEffect(() => {
    DefaultArchiveFilters();
  }, []);

  useEffect(() => {
    if (hasPageRendered.current) {
      handleClear();
    }
    hasPageRendered.current = true;
  }, [accountId, webDUser]);

  //DateOfServiceFilter
  useEffect(() => {
    setServiceDateFrom(selectedServiceDateFrom);
  }, [selectedServiceDateFrom, renderData]);

  useEffect(() => {
    setServiceDateTo(selectedServiceDateTo);
  }, [selectedServiceDateTo, renderData]);

  //DateOfDictationFilter
  useEffect(() => {
    setDictationDateFrom(selectedDictationDateFrom);
  }, [selectedDictationDateFrom, renderData]);

  useEffect(() => {
    setDictationDateTo(selectedDictationDateTo);
  }, [selectedDictationDateTo, renderData]);

  //PatientFilter
  useEffect(() => {
    setFirstName(selectedFirstName);
  }, [selectedFirstName, renderData]);

  useEffect(() => {
    setLastName(selectedLastName);
  }, [selectedLastName, renderData]);

  useEffect(() => {
    setMrn(selectedMRN);
  }, [selectedMRN, renderData]);

  useEffect(() => {
    
    setDob(selectedDOB);
  }, [selectedDOB, renderData]);

  //DictationFilter
  useEffect(() => {
    setProviders(selectedProviders);
  }, [JSON.stringify(selectedProviders), renderData]);

  useEffect(() => {
    setReportTypes(selectedReportTypes);
  }, [JSON.stringify(selectedReportTypes), renderData]);

  useEffect(() => {
    setJobId(selectedJobId);
  }, [selectedJobId, renderData]);

  //LocationFilter
  useEffect(() => {
    setRegions(selectedRegions);
  }, [JSON.stringify(selectedRegions), renderData]);

  useEffect(() => {
    setLocations(selectedLocations);
  }, [JSON.stringify(selectedLocations), renderData]);

  //JobStatusFilter
  useEffect(() => {
    setJobStatus(selectedJobStatuses);
  }, [JSON.stringify(selectedJobStatuses), renderData]);

  useEffect(() => {
    setSelectedDefaultDateType(defaultDateType);
  }, [defaultDateType]);

  const useFilterData = (viewName: string) => {
    const dateOfServiceValues = { serviceDateFrom, serviceDateTo};
    const dateOfDictationValues = { dictationDateFrom, dictationDateTo};
    const patientValues = { firstName, lastName, mrn, dob };
    const dictationValues = { providers, reportTypes, jobId };
    const locationValues = { regions, locations};
    const jobStatusValues = { jobStatus };

    return {
      selectedServiceDateFrom: dayjs(dateOfServiceValues.serviceDateFrom).format("MM/DD/YYYY"),
      selectedServiceDateTo: dayjs(dateOfServiceValues.serviceDateTo).format("MM/DD/YYYY"),
      selectedDictationDateFrom: dayjs(dateOfDictationValues.dictationDateFrom).format("MM/DD/YYYY"),
      selectedDictationDateTo: dayjs(dateOfDictationValues.dictationDateTo).format("MM/DD/YYYY"),
      selectedFirstName: patientValues.firstName,
      selectedLastName: patientValues.lastName,
      selectedMRN: patientValues.mrn,
      selectedDOB: dayjs(patientValues.dob).format("MM/DD/YYYY"),
      selectedProviders: dictationValues.providers,
      selectedReportTypes: dictationValues.reportTypes,
      selectedJobId: dictationValues.jobId,
      selectedRegions: locationValues.regions,
      selectedLocations: locationValues.locations,
      selectedJobStatuses: jobStatusValues.jobStatus,
      defaultDateType: (defaultDateTypeChecked? selectedDefaultDateType: DefaultDateType.None),
      viewName,
      userId,
      accountId
    };
  };

  const prepareFiltersData = (data: any) => {
    return {
      jobStatus: data.selectedJobStatuses || 0,
      strPatientName:
        data.selectedFirstName && data.selectedLastName
          ? `${data.selectedFirstName} ${data.selectedLastName}`
          : data.selectedFirstName || data.selectedLastName || undefined,
      strMRN: data.selectedMRN || undefined,
      strProviderId: data.selectedProviders || undefined,
      strReportType: data.selectedReportTypes || undefined,
      dtDOSFrom: dayjs(data.selectedServiceDateFrom, "MM/DD/YYYY", true).isValid()
        ? data.selectedServiceDateFrom
        : undefined,
      dtDOSTo: dayjs(data.selectedServiceDateTo, "MM/DD/YYYY", true).isValid()
        ? data.selectedServiceDateTo
        : undefined,
      dtDODFrom: dayjs(data.selectedDictationDateFrom, "MM/DD/YYYY", true).isValid()
        ? data.selectedDictationDateFrom
        : undefined,
      dtDODTo: dayjs(data.selectedDictationDateTo, "MM/DD/YYYY", true).isValid()
        ? data.selectedDictationDateTo
        : undefined,
      jobApprovalStatus: data.jobApprovalStatus || 0,
      strRegion: data.selectedRegions || undefined,
      strLocation: data.selectedLocations || undefined,
      dtPatDOB: dayjs(data.selectedDOB, "MM/DD/YYYY", true).isValid()
        ? data.selectedDOB
        : undefined,
      VTJobId: data.selectedJobId || 0,
      qcholdFilter: data.qcholdFilter || 0
    };
  };

  const handleSearch = () => {
    const filtersData = useFilterData("ArchiveSearch");
    const preparedFilterData = prepareFiltersData(filtersData);
    setCurrentFilters(preparedFilterData);
    setShowResults(true);
    onShowResultsChange(true); 
  };

  const handleClear = () => {
    setServiceDateFrom(null);
    setServiceDateTo(null);
    setDictationDateFrom(null);
    setDictationDateTo(null);
    setFirstName("");
    setLastName("");
    setMrn("");
    setDob(null);
    setProviders([]);
    setReportTypes([]);
    setJobId("");;
    setRegions([]);
    setLocations([]);
    setJobStatus([]);
    setSelectedDefaultDateType(DefaultDateType.None);
  };

  const handleBackToSearch = () => {
    setShowResults(false);
    onShowResultsChange(false); 
  };

  useEffect(() => {
    if (isModalVisible) {
      document.body.classList.add("blur");
    } else {
      document.body.classList.remove("blur");
    }

    return () => {
      document.body.classList.remove("blur");
    };
  }, [isModalVisible]);

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSaveView = () => {
    if (!viewName) {
      showErrorToast("Please enter a view name.");
      return;
    }

    const filtersData = useFilterData(viewName);

    const existingViews = JSON.parse(localStorage.getItem("archiveFilterViews")!) || [];

    existingViews.push(filtersData);

    localStorage.setItem("archiveFilterViews", JSON.stringify(existingViews));

    handleCancel();
    handleSearch();
  };

  useImperativeHandle(ref, () => ({
    showModal() {
      setViewName("");
      setIsModalVisible(true);
    }
  }));

  return (
    <>
      {isModalVisible && <div className="blur" />}

      {!showResults ? (
        <Card styles={{ body: { padding: "0px" } }} className="archive-filters-card">
          <span className="archive-filters-title">Advanced Search</span>
          <Row gutter={[8, 12]} style={{ marginTop: "20px" }} justify="space-between">
            <Col xs={24} sm={12}>
              <DateOfServiceFilter
                key={dateOfServiceFilter}
                selectedServiceDateFrom={serviceDateFrom}
                selectedServiceDateTo={serviceDateTo}
                onChangeDates={(from, to) => {
                  setServiceDateFrom(from);
                  setServiceDateTo(to);
                }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <DateOfDictationFilter
                key={dateOfDictationFilter}
                selectedDictationDateFrom={dictationDateFrom}
                selectedDictationDateTo={dictationDateTo}
                onChangeDates={(from, to) => {
                  setDictationDateFrom(from);
                  setDictationDateTo(to);
                }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <PatientFilter
                key={patientFilter}
                selectedFirstName={firstName}
                selectedLastName={lastName}
                selectedMRN={mrn}
                selectedDOB={dob}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                onMRNChange={setMrn}
                onDOBChange={setDob}
              />
            </Col>
            <Col xs={24} sm={12}>
              <DictationFilter
                key={dictationFilter}
                selectedProviders={providers}
                selectedReportTypes={reportTypes}
                selectedJobId={jobId}
                onProviderChange={setProviders}
                onReportTypeChange={setReportTypes}
                onJobIdChange={setJobId}
              />
            </Col>
            <Col xs={24} sm={12}>
              <LocationFilter
                key={locationFilter}
                selectedRegions={regions}
                selectedLocations={locations}
                onRegionsChange={setRegions}
                onLocationsChange={setLocations}
              />
            </Col>
            <Col xs={24} sm={12}>
              <JobStatusFilter
                key={jobStatusFilter}
                selectedJobStatuses={jobStatus}
                onJobStatusChange={setJobStatus}
              />
            </Col>
              <Col flex="auto" />
              <Col
                xs={24}
                sm={12}
                style={{ textAlign: "left", display: "flex"}}
                className="button-group-archive">
                <Button
                  className="search-button"
                  onClick={handleSearch}>
                  <SearchIcon width="16px" height="16px" />
                  Search
                </Button>
                <Button className="clear-button" onClick={handleClear}>
                  <CloseIcon width="16px" height="16px" />
                  Clear
                </Button>
              </Col>
          </Row>
        </Card>
      ) : (
        <div>
          {currentFilters && (
            <JobGrid preparedFilters={currentFilters} source={"Archive"} key={0} />
          )}
          <Button onClick={handleBackToSearch} style={{ marginTop: "20px" }}>
            Back to Advanced Search
          </Button>
        </div>
      )}

      <Modal
        centered={true}
        title={
          <div className="modal-header">
            <span>Save As Filter</span>
            <div className="modal-actions">
              <Button className="save-view-modal-button" onClick={handleSaveView}>
                Save Filter
              </Button>
              <AddIcon onClick={handleCancel} className="close-icon" />
            </div>
          </div>
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        closable={false}>
        <label htmlFor="view-name" className="view-name-label">
          Filter name
        </label>
        <Input
          id="view-name"
          value={viewName}
          onChange={e => setViewName(e.target.value)}
          placeholder="Enter filter name"
        />
        {selectedDefaultDateType !== DefaultDateType.None && (
          <Checkbox
            className="rolling-date-checkbox"
            onChange={(e) => setDefaultDateTypeChecked(e.target.checked)}>
            Enable Rolling Date for <b>{defaultDateType}</b>
          </Checkbox>
        )}
      </Modal>
    </>
  );
});

export default ArchiveFilters;
